import React from 'react';
import { EnvelopeSimple, Globe, DeviceMobile } from 'phosphor-react';
import './SInputField.css';
import SCountryDialCode from './SCountryDialCode';

export type SInputVariant = 'email' | 'phone' | 'country';
export type SInputState = 'default' | 'active';
export type SInputSize = 's' | 'm';
export type SInputIconPosition = 'left' | 'right';
export type SInputTheme = 'dark' | 'light';

interface SInputFieldProps {
  variant?: SInputVariant;
  state?: SInputState;
  size?: SInputSize;
  theme?: SInputTheme;
  /** Whether to show a leading icon */
  showIcon?: boolean;
  /** Optional custom icon element. If not provided, defaults to Mail icon when showIcon is true. */
  icon?: React.ReactNode;
  /** Icon position - left or right */
  iconPosition?: SInputIconPosition;
  /** If provided, shows a label row 8px above the field with this text */
  labelText?: string;
   /** Country flag emoji or image placeholder for country variant */
   countryFlag?: string;
   /** Country dial code for country variant (e.g. "+1") */
   countryDialCode?: string;
   /** Click handler for opening country dial dropdown */
   onCountryClick?: () => void;
   /** Change handler for country selection */
   onCountryChange?: (dialCode: string, flag: string) => void;
   className?: string;
   style?: React.CSSProperties;
   /** Input value */
   value?: string;
   /** Change handler */
   onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
   /** Placeholder text */
   placeholder?: string;
   /** Maximum length of input */
   maxLength?: number;
   /** Input mode hint for mobile keyboards */
   inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

export const SInputField: React.FC<SInputFieldProps> = ({
  variant = 'email',
  state = 'default',
  size = 'm',
  theme = 'dark',
  showIcon = false,
  icon,
  iconPosition = 'left',
  labelText,
  countryFlag,
  countryDialCode,
  onCountryClick,
  onCountryChange,
  className,
  style,
  maxLength,
  inputMode,
  ...inputProps
}) => {
  const rootClassNames = [
    's-input-root',
    `s-input-state-${state}`,
    `s-input-variant-${variant}`,
    `s-input-size-${size}`,
    theme === 'light' ? 's-input-theme-light' : '',
    iconPosition === 'right' ? 's-input-icon-right' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const defaultIconForVariant: Record<SInputVariant, React.ReactNode> = {
    email: <EnvelopeSimple size={20} />,
    phone: <DeviceMobile size={20} />,
    country: <Globe size={20} />,
  };

  const leadingIcon = icon ?? defaultIconForVariant[variant];

  const wrapperClass = ['s-input-wrapper', theme === 'light' ? 's-input-theme-light' : ''].filter(Boolean).join(' ');

  return (
    <div className={wrapperClass}>
      {labelText && (
        <div className="s-input-label-row">
          <span className="s-input-label body-sm">{labelText}</span>
        </div>
      )}
      {variant === 'country' ? (
        <div className={rootClassNames} style={style}>
          <SCountryDialCode
            flag={countryFlag}
            dialCode={countryDialCode}
            onClick={onCountryClick}
            onChange={onCountryChange}
          />
        </div>
      ) : (
        <div className={rootClassNames} style={style}>
          {showIcon && iconPosition === 'left' && (
            <span className="s-input-icon">
              {leadingIcon}
            </span>
          )}
          {(() => {
            const inputType = variant === 'email' ? 'email' : variant === 'phone' ? 'tel' : 'text';

            return (
              <input
                className="s-input-element"
                type={inputType}
                inputMode={inputMode}
                value={inputProps.value}
                onChange={inputProps.onChange}
                placeholder={inputProps.placeholder}
                maxLength={maxLength}
              />
            );
          })()}
          {showIcon && iconPosition === 'right' && (
            <span className="s-input-icon">
              {leadingIcon}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SInputField;
